import React, {Suspense} from 'react';
import {BrowserRouter as Router, Navigate, Route, Routes} from 'react-router-dom';
import {ThemeProvider} from '@mui/material/styles';
import {CircularProgress, CssBaseline, Box} from '@mui/material';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDateFns as DateFnsAdapter} from '@mui/x-date-pickers/AdapterDateFnsV3';
import {AuthProvider, useAuth} from './contexts/auth.context';
import {routes} from './config/app-routes.config';
import ProtectedRoute from './utils/protected-route';
import ForcePasswordChangeWrapper from './utils/force-password-change';
import MainLayout from './components/layout/main-layout.component';
import {materialTheme} from './theme/material-theme';

const SuspenseFallback = () => (
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh'}}>
        <CircularProgress/>
    </Box>
);

const AppContent: React.FC = () => {
    const {isAuthenticated} = useAuth();
    return (
        <Suspense fallback={<SuspenseFallback/>}>
            <Routes>
                {routes.map((route, index) => {
                    const routeElement = route.requiresAuth ? (
                        <ProtectedRoute>
                            <route.component/>
                        </ProtectedRoute>
                    ) : (
                        <route.component/>
                    );

                    // If user is authenticated and route requires auth, wrap in MainLayout
                    // If route doesn't require auth (like login), render without layout
                    if (route.requiresAuth && isAuthenticated) {
                        return (
                            <Route
                                key={index}
                                path={route.path}
                                element={
                                    <MainLayout>
                                        <ForcePasswordChangeWrapper>
                                            {routeElement}
                                        </ForcePasswordChangeWrapper>
                                    </MainLayout>
                                }
                            />
                        );
                    } else {
                        return (
                            <Route
                                key={index}
                                path={route.path}
                                element={routeElement}
                            />
                        );
                    }
                })}
                {/* Catch all route - redirect to home */}
                <Route path="*" element={<Navigate to="/" replace/>}/>
            </Routes>
        </Suspense>
    );
};

function App() {
    return (
        <ThemeProvider theme={materialTheme}>
            <CssBaseline/>
            <LocalizationProvider dateAdapter={DateFnsAdapter}>
                <Router>
                    <AuthProvider>
                        <AppContent/>
                    </AuthProvider>
                </Router>
            </LocalizationProvider>
        </ThemeProvider>
    );
}

export default App;
